import { ArticleCategory, ArticleSearchModel, ArticleType } from '../models/article.model';
import { Article } from '../models/article.model';
import { map, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApolloService } from './apollo.service';

@Injectable({
  providedIn: 'root',
})
export class ArticleService {
  private return = `{ 
    id, 
    slug, 
    date,
    updateDate, 
    place, 
    name, 
    draft,
    description,
    seoDescription,
    lat,
    lng,
    destinationId,
    hasSummary,
    destination{id, country, continent}, 
    coverPicture{src, width, height},
    articleType,
    category,
    sections{
      id, 
      order, 
      componentName, 
      sectionData{ 
        id,
        title,
        icon,
        isAnchor,
        text,
        postId,
        url,
        width,
        height,
        link,
        articleId,
        pictureColumns{order, pictures{id, src, name, order, width, height, legend, description}}
      }
    },
    stickyLinks{
      id,
      text,
      link,
      icon
    },
    stickyLinksTitle
  }`;

  constructor(private apolloService: ApolloService) {}

  getArticles(skip = 0, take = 10): Observable<Article[]> {
    return this.apolloService.query<Article[]>(
      `{articles(articleType: ${ArticleType.Article},skip:${skip}, take:${take})${this.return}}`,
      'articles',
    );
  }

  getArticlesWithFilter(
    category?: ArticleCategory,
    destinationId?: string,
    skip = 0,
    take = 10,
  ): Observable<Article[]> {
    let args = `articleType:${ArticleType.Article},skip:${skip},take:${take}`;
    if (category !== undefined) {
      args += `,category:${category}`;
    }
    if (destinationId) {
      args += `,destinationId:"${destinationId}"`;
    }

    return this.apolloService.query<Article[]>(`{articles(${args})${this.return}}`, 'articles');
  }

  getAllArticles(): Observable<Article[]> {
    return this.apolloService.query<Article[]>(`{allArticles${this.return}}`, 'allArticles');
  }

  getAccomodations(skip = 0, take = 10): Observable<Article[]> {
    return this.apolloService.query<Article[]>(
      `{articles(articleType: ${ArticleType.Accommodation},skip:${skip}, take:${take})${this.return}}`,
      'articles',
    );
  }

  getSetups(skip = 0, take = 10): Observable<Article[]> {
    return this.apolloService.query<Article[]>(
      `{articles(articleType: ${ArticleType.Setup},skip:${skip}, take:${take})${this.return}}`,
      'articles',
    );
  }

  getArticlesWithDraft(): Observable<Article[]> {
    return this.apolloService.query<Article[]>(
      `{articles(articleType: ${ArticleType.Article},draft: true)${this.return}}`,
      'articles',
    );
  }

  getAccomodationsWithDraft(): Observable<Article[]> {
    return this.apolloService.query<Article[]>(
      `{articles(articleType: ${ArticleType.Accommodation},draft: true)${this.return}}`,
      'articles',
    );
  }

  getSetupsWithDraft(): Observable<Article[]> {
    return this.apolloService.query<Article[]>(
      `{articles(articleType: ${ArticleType.Setup},draft: true)${this.return}}`,
      'articles',
    );
  }

  getArticle(articleId: string): Observable<Article> {
    return this.apolloService.query<Article>(
      `{article(id: "${articleId}")${this.return}}`,
      'article',
      null,
    );
  }

  getArticleBySlug(slug: string): Observable<Article> {
    return this.apolloService.query<Article>(`{article(slug: "${slug}")${this.return}}`, 'article');
  }

  getLastArticles(lastNumber = 1): Observable<Array<Article>> {
    return this.apolloService.query<Article[]>(
      `{articles(take: ${lastNumber})${this.return}}`,
      'articles',
    );
  }

  getRandomArticles(): Observable<Array<Article>> {
    return this.apolloService.query<Article[]>(`{randomArticles${this.return}}`, 'randomArticles');
  }

  searchArticle(searchModel: ArticleSearchModel): Observable<Article[]> {
    // return this.apolloService.query<Article[]>(
    //   `{searchArticle(searchInput: {articleType: ${searchModel.articleType}, searchWord: "${searchModel.searchWord}", destinationId: "${searchModel.destinationId}"})${this.return}}`,
    //   'searchArticle'
    // );
    return this.apolloService.query<Article[]>(
      `query SearchArticle($searchInput: ArticleSearchInput!){
        searchArticle(searchInput: $searchInput)${this.return}
      }`,
      'searchArticle',
      { searchInput: searchModel },
    );
  }

  getDestinationArticles(destinationId: string, skip = 0, take = 10): Observable<Article[]> {
    return this.apolloService.query<Article[]>(
      `{destinationArticles(destinationId: "${destinationId}", articleType: ${ArticleType.Article},skip: ${skip},take: ${take})${this.return}}`,
      'destinationArticles',
    );
  }

  createArticle(article: Article): Observable<Article> {
    return this.apolloService.mutate<Article>(
      `CreateArticle($article: CreateArticleInput!){
        createArticle(createArticleInput: $article)${this.return}
      }`,
      'createArticle',
      { article },
    );
  }

  updateArticle(article: Article): Observable<Article> {
    delete article.destination;
    return this.apolloService.mutate<Article>(
      `UpdateArticle($article: UpdateArticleInput!){
        updateArticle(updateArticleInput: $article)${this.return}
      }`,
      'updateArticle',
      { article },
    );
  }

  deleteArticle(articleId: string): Observable<boolean> {
    return this.apolloService
      .mutate<number>(
        `RemoveArticle($articleId: UUID!) {
                removeArticle(id: $articleId)
            }`,
        'removeArticle',
        { articleId },
      )
      .pipe(map((x) => x > 0));
  }
}
